import * as React from 'react';
import { Spinner, Intent, Button, H4, Tooltip, Icon, Elevation, Card } from '@blueprintjs/core';
import { UserState } from 'app/reducers/state';
import { executeQueryPostAuth, executeQueryGetAuth, API_CHARTBACK } from 'app/utils/api';
import { KryPlayDetails } from 'app/components/KryPlayDetails';
import { each, clone } from 'lodash';
import { CoachingAudio } from 'app/components/CoachingAudio';
import * as superagent from 'superagent';
import Highcharts from 'highcharts';
import ReactHighcharts from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import hcXrange from 'highcharts/modules/xrange';
import { translateKey } from 'app/utils/forms';
import TextAreaV2 from 'app/components/TextAreaV2';

highchartsMore(Highcharts);
(hcXrange)(Highcharts)

const GOOD_CUTOFF = 0.50;

interface Props {
    objectiveData?: any;
    gameID: string;
    user: UserState;
    needsJournal: boolean;
    menuConfig: any;
    savedPreJournal: string;
    failureMessage: string;
    successMessage: string;
    onResume: () => void;
    onSaveJournal: (preFeedback: string, redo: boolean) => void;
};

interface State {
    loadingObjectiveData: boolean;
    objectiveData: any;
    writtenJouranl: string;
    savingJournal: boolean;
    coachingAudio: string | string[];
    showCharts: boolean;
    showScore: boolean;
    loadingCharts: boolean;
    chartData: any;
    commitmentScore: number;
    businessScore: number;
    sub1Score: number;
    sub2Score: number;
    sub3Score: number;
    opinionTooltips: any;
    tensionTooltips: any;
    isPassing: boolean;
}

export class ClassicLPResultsScreen extends React.Component<Props, State> {

    state: State = {
        loadingObjectiveData: true,
        objectiveData: {},
        writtenJouranl: '',
        savingJournal: false,
        coachingAudio: '',
        showCharts: true,
        loadingCharts: true,
        showScore: true,
        chartData: [],
        commitmentScore: 50.0,
        businessScore: 30.0,
        sub1Score: 15.0,
        sub2Score: 40.0,
        sub3Score: 32.0,
        opinionTooltips: {},
        tensionTooltips: {},
        isPassing: false
    }

    getPrompt = () => {
        const {
            menuConfig
        } = this.props;

        return menuConfig.postJournalTitle;
    }

    getChartData = () => {

        const {
            opinionTooltips,
            tensionTooltips
        } = this.state;

        // Inject style settings
        let dataClone = clone(this.state.chartData);

        if (dataClone['chart'] === undefined) {
            dataClone['chart'] = {};
        }

        // Inject a full page height to our results chart
        dataClone['chart']['height'] = 'auto';

        dataClone['tooltip'] = {
            formatter: function () {
                const round = this.x;
                const roundedY = Math.round(parseFloat(this.y));

                // Opinion
                if (this.color.toLowerCase() === '#00CB00'.toLowerCase()) {
                    return opinionTooltips[round.toString()];
                }

                // Tension
                if (this.color.toLowerCase() === '#FF9319'.toLowerCase()) {
                    return tensionTooltips[round.toString()];
                }

                // PI
                if (this.color.toLowerCase() === '#935730'.toLowerCase()) {
                    return '<b>Influence: ' + roundedY.toString() + '</b>';
                }

                return '3';
            }
        }

        return dataClone;
    }

    componentDidMount() {
        window.addEventListener('resize', ()=> {
            try {
                // Make sure the highchart fits correctly
                Highcharts.charts.forEach(chart => chart!.reflow());
            }
            catch(e) {
                // Let this fail without disrupting the page
                console.error(e);
            }
        });

        executeQueryPostAuth("data/results/" + this.props.gameID, {}, this.props.user, (postResponse) => {
            executeQueryGetAuth("data/results/" + this.props.gameID, {}, this.props.user, (objectiveResponse) => {
                const response = JSON.parse(objectiveResponse.text);

                let anyOutcomesFailing = false;
                let processTotal = 0;
                let processPositive = 0;

                each(response.objectives, objective => {
                    const categoryname = objective.category;
                    const isSuccessfull = objective.result === "True" || objective.result === "true";
        
                    if (categoryname === undefined) return;
        
                    if (categoryname.includes("Outcome")) {
                        if (!isSuccessfull) {
                            anyOutcomesFailing = true;
                        }
                    }
                    else {
                        processTotal += 1;
        
                        if (isSuccessfull) {
                            processPositive += 1;
                        }
                    }
                });

                const processAsPercent = processPositive / processTotal;

                this.setState({
                    loadingObjectiveData: false,
                    objectiveData: response,
                    isPassing: anyOutcomesFailing === false && (processAsPercent >= 0.40)
                }, () => {

                    const {
                        menuConfig
                    } = this.props;
            
                    if (menuConfig.blockObjectives === true) {
                        this.setState({
                            showCharts: false
                        });
                    }
                });
            });
        });
    }

    // Ignoring this for now
    componentDidUpdate() {
        // if (this.props.objectiveData !== undefined) {
        //     if (isEqual(this.props.objectiveData, this.state.objectiveData) === false) {
        //         this.setState({
        //             objectiveData: this.props.objectiveData
        //         });
        //     }
        // }
    }

    getAudioConfig = () => {
        const {
            menuConfig
        } = this.props;
        /*
            Possible audio entries
            good_goal: undefined,
            bad_goal: undefined,
            neutral_goal: undefined,
            good_process: undefined,
            bad_process: undefined,
            neutral_process: undefined
        */

        if (menuConfig === undefined) return {};

        if (menuConfig['outcomeaudio'] === undefined) return {};

        return menuConfig['outcomeaudio'];
    }

    processCoachingTriggers = (objectiveData) => {
        const audioConfig = this.getAudioConfig();

        let stats = {
            outcome_total: 0,
            outcome_positive: 0,
            outcome_result: 0.0,
            process_total: 0,
            process_positive: 0,
            process_result: 0.0,
        }

        each(objectiveData.objectives, objective => {
            const categoryname = objective.category;
            const isSuccessfull = objective.result === "True" || objective.result === "true";

            if (categoryname === undefined) return;

            if (categoryname.includes("Outcome")) {
                stats.outcome_total += 1;

                if (isSuccessfull) {
                    stats.outcome_positive += 1;
                }
            }
            else {
                stats.process_total += 1;

                if (isSuccessfull) {
                    stats.process_positive += 1;
                }
            }
        });

        // Calculate our results
        stats.outcome_result = stats.outcome_positive / stats.outcome_total;
        stats.process_result = stats.process_positive / stats.process_total;

        const isOutcomeGood = (stats.outcome_result >= GOOD_CUTOFF);
        const isProcessGood = (stats.process_result >= GOOD_CUTOFF);

        // TODO: Process outcome as well
        const audioLocationInMap        = isProcessGood ? 'good_process' : 'bad_process';
        const audioLocationInMapOutcome = isOutcomeGood ? 'good_goal'    : 'bad_goal';

        let audioLocations: string[] = [];

        if(audioConfig[audioLocationInMapOutcome] !== undefined) {
            audioLocations.push(audioConfig[audioLocationInMapOutcome]);
        }

        if (audioConfig[audioLocationInMap] !== undefined) {
            audioLocations.push(audioConfig[audioLocationInMap]);
        }

        // Now add all of our "additionalAudio" pieces
        each(audioConfig['additionalAudio'], optionalAudio => {
            audioLocations.push(
                optionalAudio
            );
        });

        if (audioLocations.length > 0) {
            this.setState({
                coachingAudio: audioLocations
            });
        }
    }

    getCurrentRound = () => {
        const {
            objectiveData
        } = this.state;

        return parseInt(objectiveData['total-rounds']);
    }

    getJournalWarning = () => {
        const {
            writtenJouranl
        } = this.state;

        const wordCount = writtenJouranl.split(' ').length;

        if (wordCount > 10) return undefined;

        return 'You might want to reflect a little more.';
    }

    renderJournal = () => {
        const {
            savingJournal
        } = this.state;

        if (savingJournal) {
            return <span>
                <Spinner />
            </span>
        }

        return <span>
            <H4>{this.getPrompt()}</H4>
            <TextAreaV2 onChange={(event) => {
                this.setState({
                    writtenJouranl: event.target.value
                });
            }} className={"journalTextArea"} rows={15} style={{width: '100%', height: '200px'}}></TextAreaV2>
        </span>
    }

    handleReplayButtonPress = () => {
        const {
            onResume,
            needsJournal,
            onSaveJournal
        } = this.props;

        this.setState({
            loadingObjectiveData: true
        });

        const {
            writtenJouranl,
            isPassing
        } = this.state;

        if (needsJournal === false) {
            onResume();
            return;
        }

        // Save our pass/fail condition on the backend
        // Then push out the journal and move on
        superagent.
            get(API_CHARTBACK() + '?userID=' + this.props.user.id + '&gameID=' + this.props.gameID + "&mock=record_lp&pass=" + isPassing.toString()).
            end((error:any, response:any) => {
                onSaveJournal(writtenJouranl, true);
            }); 
    }

    handleResumeButtonPress = () => {
        const {
            onResume,
            needsJournal,
            onSaveJournal
        } = this.props;

        const {
            writtenJouranl,
            isPassing
        } = this.state;

        this.setState({
            loadingObjectiveData: true
        });

        if (needsJournal === false) {
            onResume();
            return;
        }

                // Save our pass/fail condition on the backend
        // Then push out the journal and move on
        superagent.
            get(API_CHARTBACK() + '?userID=' + this.props.user.id + '&gameID=' + this.props.gameID + "&mock=record_lp&pass=" + isPassing.toString()).
            end((error:any, response:any) => {
                onSaveJournal(writtenJouranl, false);
            }); 
    }

    renderChevron = (active: boolean) => {
        if (active === false) {
            return <span style={{
                float: 'left',
                display: 'block',
                height: '10px',
                width: '48px'
            }}>
            </span>
        }

        return <span>
            <Icon iconSize={32} style={{marginRight: '8px', marginLeft: '8px', marginTop: '10px', float: 'left'}} icon='chevron-right' />
        </span>
    }

    renderReadYourOriginalPlanButton = () => {
        const {
            showCharts,
            showScore
        } = this.state;

        const isRendering = (showCharts === false && showScore === false);

        return <span style={{display: 'block'}}>
            {this.renderChevron(isRendering)}
            <Button onClick={() => {
                this.setState({showCharts: false, showScore: false});
            }} large style={{display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px'}} intent={Intent.PRIMARY}>{translateKey('vleader.originalPlan')}</Button>
        </span>
    }

    renderScoreHappenedButton = () => {
        const {
            showScore
        } = this.state;

        return <span style={{display: 'block'}}>
            {this.renderChevron(showScore)}
            <Button onClick={() => {
                this.setState({showScore: true, showCharts: false});
            }} large style={{display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px'}} intent={Intent.PRIMARY}>{translateKey('vleader.yourResults')}</Button>
        </span>
    }

    renderReflectOnWhatHappenedButton = () => {
        const {
            showCharts
        } = this.state;

        return <span style={{display: 'block'}}>
            {this.renderChevron(showCharts)}
            <Button onClick={() => {
                this.setState({showCharts: true, showScore: false});
            }} large style={{display: 'block', width: '350px', marginTop: '10px', height: '50px', marginLeft: '20px'}} intent={Intent.PRIMARY}>{translateKey('vleader.reflect')}</Button>
        </span>
    }

    renderResumeButton = () => {
        const {
            needsJournal,
            menuConfig
        } = this.props;

        const RESULTS_SCREEN_BUTTON_STYLE = {display: 'block', width: '350px', marginTop: '10px', height: '60px', marginLeft: '50px'};

        const {
            writtenJouranl
        } = this.state;

        if (needsJournal === false) {
            return <Button onClick={this.handleResumeButtonPress} style={RESULTS_SCREEN_BUTTON_STYLE} large intent={Intent.SUCCESS}>{translateKey('vleader.savePlay')}</Button>
        }

        const journalWarning = this.getJournalWarning();

        const redoButton = (menuConfig.blockRedo === true) ? undefined : <Tooltip content={journalWarning}>
            <Button onClick={this.handleReplayButtonPress} style={RESULTS_SCREEN_BUTTON_STYLE} disabled={writtenJouranl === ''} large intent={Intent.SUCCESS}>{translateKey('vleader.saveReplay')}</Button>
        </Tooltip>;

        return <span>
            {redoButton}
            <Tooltip content={journalWarning}>
                <Button onClick={this.handleResumeButtonPress} style={RESULTS_SCREEN_BUTTON_STYLE} disabled={writtenJouranl === ''} large intent={Intent.SUCCESS}>{translateKey('vleader.savePlay')}</Button>
            </Tooltip>
        </span>
    }

    renderAudio = () => {
        const {
            coachingAudio,
            loadingObjectiveData
        } = this.state;

        if (loadingObjectiveData) {
            return <span></span>
        }

        if (coachingAudio === undefined || coachingAudio === '') {
            return <span></span>
        }

        return <CoachingAudio 
            audioSource={coachingAudio}
            donePlaying={() => {
                
            }}
        />
    }

    renderPlayDetails = () => {
        const {
            menuConfig
        } = this.props;

        const {
            objectiveData 
        } = this.state;

        if (menuConfig.blockObjectives === true) {
            return <span></span>
        }

        return <KryPlayDetails 
            user={this.props.user}
            selectedGame={objectiveData}
            canEditJournal={false}
            inSimulator={true}
            updateJournalDataInline={(stage: string, payload: string, gameID: string) => {
                // ignore this, we aren't letting them edit in this mode
            }}
        />
    }

    renderScoreDisplay = () => {
        const {
            isPassing
        } = this.state;

        const {
            successMessage,
            failureMessage
        } = this.props;

        const passGraphic = <span><Card style={{backgroundColor: '#a5f0b4', width: '300px', textAlign: 'center', margin: 'auto'}} interactive={false} elevation={Elevation.TWO}>
                <h1>{translateKey('vleader.pass')}</h1>
            </Card>
            <p style={{marginTop: '20px', textAlign: 'center'}}>
                {successMessage}
            </p>
        </span>

        const failGraphic = <span>
            <Card style={{backgroundColor: '#ffceb3', width: '300px', textAlign: 'center', margin: 'auto'}} interactive={false} elevation={Elevation.TWO}>
                <h1>{translateKey('vleader.fail')}</h1>
            </Card>            
            <p style={{marginTop: '20px', textAlign: 'center'}}>
                {failureMessage}
            </p>
        </span>

        return <span style={{flexGrow: 1, flexBasis: 0}}>
            <span style={{flexShrink: 1, transition: 'all 2s'}}>
                <span style={{position: 'relative'}}>
                    {isPassing ? passGraphic : failGraphic}
                </span>
            </span>
        </span>
    }

    renderDisplay = () => {
        const {
            showCharts,
            showScore
        } = this.state;

        const chartData = this.getChartData();

        const {
            savedPreJournal
        } = this.props;

        if (showCharts === true) {
            return <span className="resultsClassicGraph" style={{flexGrow: 1, flexBasis: 0}}>
                <div style={{display: 'block', height: '100%'}}>
                    <ReactHighcharts containerProps={{ style: { height: "100%" } }} highcharts={Highcharts} options={chartData} />
                </div>
            </span>
        }

        if (showScore) {
            return this.renderScoreDisplay();
        }
        
        // Show the user what they entered as there plan
        return <span style={{flexGrow: 1, flexBasis: 0}}>
            <span style={{flexShrink: 1, transition: 'all 2s'}}>
                <span style={{position: 'relative'}}>
                    <H4>{translateKey('vleader.yourJournal')}</H4>
                    <TextAreaV2 defaultValue={savedPreJournal} value={savedPreJournal} readOnly={true} className={"journalTextArea"} rows={15} style={{width: '100%', height: '180px'}}></TextAreaV2>
                </span>
            </span>
        </span>
    }

    renderControls = () => {
        return <span style={{flexGrow: 0}}>
            <span style={{display: 'flex', flexDirection: 'row'}}>
                <span style={{flexGrow: 1}}>    
                    {this.renderJournal()}
                </span>
                <span style={{width: '400px'}}>
                    {this.renderScoreHappenedButton()}
                    {this.renderReadYourOriginalPlanButton()}
                    {this.renderResumeButton()}
                </span>
            </span>
        </span>
    }

	render() {
        const {
            loadingObjectiveData
        } = this.state;

        if (loadingObjectiveData) {
            return <span style={{display: 'block', width: '100%', height: '100%'}}>
                <span style={{display: 'block', width: '200px', height: '300px', margin: 'auto', marginTop: '100px'}}>
                    <H4 style={{textAlign: 'center', marginBottom: '40px'}}>{translateKey('vleader.loading')}</H4>
                    <Spinner />
                </span>
            </span>
        }

        return <span style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            {this.renderAudio()}
            {this.renderDisplay()}
            {this.renderControls()}
            <div className="clearfix" />
        </span>
    }
}
